import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import Header from './Header';
import Footer from './Footer';
import FreeShippingBadge from './FreeShippingBadge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ShoppingBag, Truck, FileText, Check, Loader2, ArrowLeft, Clock, AlertTriangle, Building2, Banknote, CheckCircle2 } from 'lucide-react';
import PhoneInput from './PhoneInput';
import api from '../services/api';
import { toast } from 'sonner';
import { trackBeginCheckout } from '../utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutPage = () => {
  const { t, currentLanguage } = useLanguage();
  const { cartItems, getCartTotal, clearCart, loadCart } = useCart();
  const { formatPrice } = useCurrency();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1); // 1: Shipping, 2: Review, 3: Confirmation
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [order, setOrder] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [entityType, setEntityType] = useState('');
  
  const [shippingInfo, setShippingInfo] = useState({
    full_name: user?.name || '',
    email: user?.email || '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'Spain',
    organization_name: '',
    organization_type: ''
  });

  const content = {
    en: {
      title: "Submit Order Enquiry",
      shippingStep: "Shipping Information",
      review: "Review Enquiry",
      confirmation: "Enquiry Submitted",
      fullName: "Full Name",
      email: "Email",
      phone: "Phone Number",
      address1: "Address Line 1",
      address2: "Address Line 2 (Optional)",
      city: "City",
      state: "State/Province",
      postalCode: "Postal Code",
      country: "Country",
      continueToReview: "Continue to Review",
      placeOrder: "Submit Order Enquiry",
      backToCart: "Back to Cart",
      backToShipping: "Back to Shipping",
      orderSummary: "Order Summary",
      subtotal: "Subtotal",
      shippingCost: "Shipping",
      freeShipping: "Free",
      total: "Total",
      items: "items",
      thankYou: "Your order enquiry has been submitted!",
      orderNumber: "Enquiry Reference",
      orderEmail: "We've sent a confirmation to",
      continueShopping: "Continue Shopping",
      viewOrders: "View Orders",
      loginRequired: "Please log in to checkout",
      emptyCart: "Your cart is empty",
      taxIncluded: "Tax Included",
      freeShippingNote: "Flat shipping: €25 under €100 · Free over €100",
      secureCheckout: "Bank Transfer Only",
      // Enquiry confirmation
      enquiryNote: "Your order is now pending KYC review. Once approved, we will issue an invoice for bank transfer payment.",
      pendingReview: "Pending KYC Review",
      whatHappensNext: "What happens next?",
      step1Next: "Our team will review your order and verify your credentials (24-48 hours)",
      step2Next: "Once approved, you will receive an invoice via email",
      step3Next: "Complete the bank transfer as per the invoice instructions",
      step4Next: "Your order will be shipped within 3-5 working days after payment confirmation",
      // Authorization section
      authorizationTitle: "Authorized Purchaser Declaration",
      authorizationWarning: "IMPORTANT: Licensed Medical Professionals Only",
      kycNotice: "To ensure compliance and quality care, we exclusively supply licensed medical professionals with high-quality wellness products. Orders will be processed after KYC approval. Verification is typically completed within 24-48 hours.",
      organizationName: "Organization/Institution Name",
      organizationType: "Organization Type",
      selectType: "Select organization type...",
      laboratory: "Research Laboratory",
      clinic: "Medical Clinic",
      university: "University/Academic Institution",
      pharmaceutical: "Pharmaceutical Company",
      other: "Other Authorized Entity",
      termsAgreement: "I confirm that I represent an authorized entity (research laboratory, medical clinic, or other licensed institution) with the legal permission to purchase and handle research compounds. I understand that:",
      term1: "All products are for research purposes only and NOT for human consumption",
      term2: "My organization holds the necessary licenses and permits to purchase these products",
      term3: "Orders will be processed only after KYC verification approval (24-48 hours)",
      term4: "I accept full responsibility for the proper handling and use of these products",
      term5: "PenLab Peptide is not responsible for any misuse by unauthorized entities",
      paymentNote: "Payment is by Bank Transfer only. Invoice will be issued after KYC approval.",
      mustAgree: "You must agree to the terms and select your organization type to continue"
    },
    es: {
      title: "Enviar Solicitud de Pedido",
      shippingStep: "Información de Envío",
      review: "Revisar Solicitud",
      confirmation: "Solicitud Enviada",
      fullName: "Nombre Completo",
      email: "Correo Electrónico",
      phone: "Número de Teléfono",
      address1: "Dirección Línea 1",
      address2: "Dirección Línea 2 (Opcional)",
      city: "Ciudad",
      state: "Provincia",
      postalCode: "Código Postal",
      country: "País",
      continueToReview: "Continuar a Revisión",
      placeOrder: "Enviar Solicitud de Pedido",
      backToCart: "Volver al Carrito",
      backToShipping: "Volver a Envío",
      orderSummary: "Resumen del Pedido",
      subtotal: "Subtotal",
      shippingCost: "Envío",
      freeShipping: "Gratis",
      total: "Total",
      items: "artículos",
      thankYou: "¡Su solicitud de pedido ha sido enviada!",
      orderNumber: "Referencia de Solicitud",
      orderEmail: "Hemos enviado una confirmación a",
      continueShopping: "Continuar Comprando",
      viewOrders: "Ver Pedidos",
      loginRequired: "Por favor inicie sesión para finalizar la compra",
      emptyCart: "Su carrito está vacío",
      taxIncluded: "Impuestos Incluidos",
      freeShippingNote: "Envío plano: €25 por debajo de €100 · Gratis a partir de €100",
      secureCheckout: "Solo Transferencia Bancaria",
      // Enquiry confirmation
      enquiryNote: "Su pedido está pendiente de revisión KYC. Una vez aprobado, emitiremos una factura para el pago por transferencia bancaria.",
      pendingReview: "Pendiente de Revisión KYC",
      whatHappensNext: "¿Qué sucede ahora?",
      step1Next: "Nuestro equipo revisará su pedido y verificará sus credenciales (24-48 horas)",
      step2Next: "Una vez aprobado, recibirá una factura por correo electrónico",
      step3Next: "Complete la transferencia bancaria según las instrucciones de la factura",
      step4Next: "Su pedido será enviado dentro de 3-5 días hábiles después de la confirmación del pago",
      // Authorization section
      authorizationTitle: "Declaración de Comprador Autorizado",
      authorizationWarning: "IMPORTANTE: Solo Profesionales Médicos Autorizados",
      kycNotice: "Para garantizar el cumplimiento y la atención de calidad, suministramos exclusivamente a profesionales médicos autorizados. Los pedidos se procesarán después de la aprobación KYC. La verificación se completa normalmente en 24-48 horas.",
      organizationName: "Nombre de la Organización/Institución",
      organizationType: "Tipo de Organización",
      selectType: "Seleccione tipo de organización...",
      laboratory: "Laboratorio de Investigación",
      clinic: "Clínica Médica",
      university: "Universidad/Institución Académica",
      pharmaceutical: "Empresa Farmacéutica",
      other: "Otra Entidad Autorizada",
      termsAgreement: "Confirmo que represento a una entidad autorizada (laboratorio de investigación, clínica médica u otra institución autorizada) con permiso legal para comprar y manejar compuestos de investigación. Entiendo que:",
      term1: "Todos los productos son solo para investigación y NO para consumo humano",
      term2: "Mi organización posee las licencias y permisos necesarios para comprar estos productos",
      term3: "Los pedidos se procesarán solo después de la verificación KYC (24-48 horas)",
      term4: "Acepto total responsabilidad por el manejo y uso adecuado de estos productos",
      term5: "PenLab Peptide no es responsable por el mal uso por entidades no autorizadas",
      paymentNote: "El pago es solo por Transferencia Bancaria. La factura se emitirá después de la aprobación KYC.",
      mustAgree: "Debe aceptar los términos y seleccionar el tipo de organización para continuar"
    },
    ru: {
      title: "Отправить заявку на заказ",
      shippingStep: "Информация о доставке",
      review: "Проверка заявки",
      confirmation: "Заявка отправлена",
      fullName: "ФИО",
      email: "Электронная почта",
      phone: "Номер телефона",
      address1: "Адрес (строка 1)",
      address2: "Адрес (строка 2, необязательно)",
      city: "Город",
      state: "Область/регион",
      postalCode: "Почтовый индекс",
      country: "Страна",
      continueToReview: "Перейти к проверке",
      placeOrder: "Отправить заявку",
      backToCart: "Вернуться в корзину",
      backToShipping: "Назад к доставке",
      orderSummary: "Сводка заказа",
      subtotal: "Промежуточный итог",
      shippingCost: "Доставка",
      freeShipping: "Бесплатно",
      total: "Итого",
      items: "товаров",
      thankYou: "Ваша заявка успешно отправлена!",
      orderNumber: "Номер заявки",
      orderEmail: "Подтверждение отправлено на",
      continueShopping: "Продолжить покупки",
      viewOrders: "Мои заказы",
      loginRequired: "Пожалуйста, войдите для оформления заказа",
      emptyCart: "Ваша корзина пуста",
      taxIncluded: "Налоги включены",
      freeShippingNote: "Доставка 25 € при заказе до 100 € · Бесплатно от 100 €",
      secureCheckout: "Только банковский перевод",
      // Enquiry confirmation
      enquiryNote: "Ваш заказ ожидает KYC-верификации. После одобрения мы выставим счёт для банковского перевода.",
      pendingReview: "Ожидает KYC-верификации",
      whatHappensNext: "Что будет дальше?",
      step1Next: "Наша команда рассмотрит ваш заказ и проверит данные (24-48 часов)",
      step2Next: "После одобрения вы получите счёт по электронной почте",
      step3Next: "Выполните банковский перевод согласно инструкциям в счёте",
      step4Next: "Ваш заказ будет отправлен в течение 3-5 рабочих дней после подтверждения оплаты",
      // Authorization section
      authorizationTitle: "Декларация уполномоченного покупателя",
      authorizationWarning: "ВАЖНО: Только для лицензированных медицинских специалистов",
      kycNotice: "Для обеспечения соответствия требованиям и качественного обслуживания мы осуществляем поставки исключительно лицензированным специалистам. Заказы обрабатываются после прохождения KYC-верификации (обычно 24-48 часов).",
      organizationName: "Название организации/учреждения",
      organizationType: "Тип организации",
      selectType: "Выберите тип организации...",
      laboratory: "Исследовательская лаборатория",
      clinic: "Медицинская клиника",
      university: "Университет/научное учреждение",
      pharmaceutical: "Фармацевтическая компания",
      other: "Другое аккредитованное учреждение",
      termsAgreement: "Я подтверждаю, что представляю аккредитованное учреждение (исследовательскую лабораторию, медицинскую клинику или иную лицензированную организацию) с законным правом на приобретение и обращение с исследовательскими соединениями. Я понимаю, что:",
      term1: "Вся продукция предназначена исключительно для исследований и НЕ подлежит употреблению человеком",
      term2: "Моя организация имеет необходимые лицензии и разрешения на приобретение данной продукции",
      term3: "Заказы обрабатываются только после прохождения KYC-верификации (24-48 часов)",
      term4: "Я несу полную ответственность за надлежащее обращение и использование данной продукции",
      term5: "PenLab Peptide не несёт ответственности за ненадлежащее использование неуполномоченными лицами",
      paymentNote: "Оплата только банковским переводом. Счёт выставляется после прохождения KYC-верификации.",
      mustAgree: "Для продолжения необходимо принять условия и выбрать тип организации"
    }
  };

  const c = content[currentLanguage] || content.en;

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/checkout');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (user) {
      setShippingInfo(prev => ({
        ...prev,
        full_name: user.name || prev.full_name,
        email: user.email || prev.email,
        phone: user.phone || prev.phone,
        organization_name: user.organization_name || prev.organization_name,
        address_line1: user.address_line1 || prev.address_line1,
        address_line2: user.address_line2 || prev.address_line2,
        city: user.city || prev.city,
        state: user.state || prev.state,
        postal_code: user.postal_code || prev.postal_code,
        country: user.country || prev.country
      }));
      // Pre-populate organization type from user profile
      if (user.organization_type && !entityType) {
        setEntityType(user.organization_type);
      }
    }
  }, [user]);

  const subtotal = getCartTotal();
  const shippingCost = subtotal >= 100 ? 0 : 25;
  const total = subtotal + shippingCost;

  // GA4 begin_checkout + Yandex.Metrica ecommerce checkout-step-1 +
  // reachGoal('begin_checkout'). Fires ONCE per cart-load when the user
  // lands on /checkout with a non-empty cart. The dependency array uses
  // cartItems.length so it doesn't re-fire on every keystroke as the
  // shipping form is filled in. Critical for Google Ads' funnel-step
  // optimisation (begin_checkout → purchase is the canonical conversion
  // path Performance Max optimises against).
  useEffect(() => {
    if (cartItems && cartItems.length > 0 && step === 1) {
      try {
        trackBeginCheckout(cartItems, total, currentLanguage);
      } catch (_) { /* analytics never blocks UX */ }
    }
  }, [cartItems.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setShippingInfo(prev => ({ ...prev, [name]: value }));
  };

  const validateShipping = () => {
    const required = ['full_name', 'email', 'phone', 'address_line1', 'city', 'postal_code', 'country', 'organization_name'];
    for (const field of required) {
      if (!shippingInfo[field]) {
        setError(`Please fill in ${field.replace(/_/g, ' ')}`);
        return false;
      }
    }
    
    // Check authorization
    if (!entityType) {
      setError(c.mustAgree);
      return false;
    }
    if (!agreedToTerms) {
      setError(c.mustAgree);
      return false;
    }
    
    setError('');
    return true;
  };

  const handleContinueToReview = () => {
    if (validateShipping()) {
      // Store entity type in shipping info
      setShippingInfo(prev => ({ ...prev, organization_type: entityType }));
      setStep(2);
    }
  };

  // Prepare order data for email (used for both success and failure)
  const prepareOrderDataForEmail = () => {
    return {
      shipping_address: { ...shippingInfo, organization_type: entityType },
      items: cartItems.map(item => ({
        product_id: item.product_id,
        product_name: item.product?.name || {},
        quantity: item.quantity,
        price: item.product?.price || 0,
        variant: item.variant
      })),
      subtotal: subtotal,
      shipping_cost: shippingCost,
      total: total,
      notes: `Authorized Entity: ${entityType}`
    };
  };

  const handlePlaceOrder = async () => {
    try {
      setLoading(true);
      setError('');

      // Create the order enquiry (no Stripe payment)
      // Auto-apply affiliate referral code if a 90-day attribution cookie exists
      let promoCode = null;
      try {
        const stored = localStorage.getItem('plp_ref');
        if (stored) promoCode = stored;
        else {
          const m = document.cookie.match(/(?:^|; )plp_ref=([^;]+)/);
          if (m) promoCode = decodeURIComponent(m[1]);
        }
      } catch {}

      const orderResponse = await api.post('/orders', {
        shipping_address: { ...shippingInfo, organization_type: entityType },
        notes: `Authorized Entity: ${entityType}`,
        promo_code: promoCode || undefined,
      });

      const createdOrder = orderResponse.data;
      setOrder(createdOrder);
      
      // Show success toast popup
      toast.success(
        currentLanguage === 'es' 
          ? '¡Gracias por su pedido! Lo revisaremos y confirmaremos en breve.' 
          : currentLanguage === 'ru'
            ? 'Спасибо за ваш заказ! Мы рассмотрим и подтвердим в ближайшее время.'
            : 'Thank you for your order! We will review and confirm shortly.',
        {
          duration: 5000,
          icon: <CheckCircle2 className="w-5 h-5 text-green-500" />,
        }
      );
      
      setStep(3); // Go directly to confirmation
      setLoading(false);
    } catch (err) {
      console.error('Order enquiry creation failed:', err);
      
      // Send failed order notification email
      try {
        const orderData = prepareOrderDataForEmail();
        await api.post('/orders/failed-notification', {
          ...orderData,
          error_message: err.response?.data?.detail || err.message || 'Unknown error',
          user_email: user?.email || shippingInfo.email
        });
      } catch (emailErr) {
        console.error('Failed to send failure notification:', emailErr);
      }
      
      setError(err.response?.data?.detail || 'Failed to submit order enquiry. Please try again.');
      setLoading(false);
    }
  };

  const getProductName = (item) => {
    const name = item.product?.name;
    if (!name) return 'Product';
    return name[currentLanguage] || name.en || name.es || 'Product';
  };

  if (!isAuthenticated) {
    return null;
  }

  if (cartItems.length === 0 && step !== 3) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-capsule-bg to-white">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{c.emptyCart}</h1>
          <Button onClick={() => navigate('/products')} className="bg-capsule-teal hover:bg-capsule-teal-dark text-white">
            {c.continueShopping}
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-capsule-bg to-white">
      <Header />
      
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="max-w-6xl mx-auto">
          {/* Progress Steps - Responsive */}
          <div className="flex items-center justify-center mb-6 sm:mb-8 overflow-x-auto pb-2">
            <div className="flex items-center min-w-max">
              <div className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full ${step >= 1 ? 'bg-capsule-teal text-white' : 'bg-gray-200 text-gray-500'}`}>
                  {step > 1 ? <Check className="w-4 h-4 sm:w-5 sm:h-5" /> : <Truck className="w-4 h-4 sm:w-5 sm:h-5" />}
                </div>
                <span className={`ml-2 text-sm sm:text-base font-medium hidden sm:inline ${step >= 1 ? 'text-capsule-teal' : 'text-gray-500'}`}>{c.shippingStep}</span>
              </div>
              <div className={`w-8 sm:w-16 h-1 mx-2 sm:mx-4 ${step >= 2 ? 'bg-capsule-teal' : 'bg-gray-200'}`} />
              <div className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full ${step >= 2 ? 'bg-capsule-teal text-white' : 'bg-gray-200 text-gray-500'}`}>
                  {step > 2 ? <Check className="w-4 h-4 sm:w-5 sm:h-5" /> : <FileText className="w-4 h-4 sm:w-5 sm:h-5" />}
                </div>
                <span className={`ml-2 text-sm sm:text-base font-medium hidden sm:inline ${step >= 2 ? 'text-capsule-teal' : 'text-gray-500'}`}>{c.review}</span>
              </div>
              <div className={`w-8 sm:w-16 h-1 mx-2 sm:mx-4 ${step >= 3 ? 'bg-capsule-teal' : 'bg-gray-200'}`} />
              <div className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full ${step >= 3 ? 'bg-capsule-teal text-white' : 'bg-gray-200 text-gray-500'}`}>
                  <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
                <span className={`ml-2 text-sm sm:text-base font-medium hidden sm:inline ${step >= 3 ? 'text-capsule-teal' : 'text-gray-500'}`}>{c.confirmation}</span>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-center">
              {error}
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Step 1: Shipping */}
              {step === 1 && (
                <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2">
                    <Truck className="w-5 h-5 text-capsule-teal" />
                    {c.shippingStep}
                  </h2>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="full_name">{c.fullName} *</Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      value={shippingInfo.full_name}
                      onChange={handleInputChange}
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">{c.email} *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={shippingInfo.email}
                      onChange={handleInputChange}
                      className="mt-1"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="phone">{c.phone} *</Label>
                    <PhoneInput
                      value={shippingInfo.phone}
                      onChange={handleInputChange}
                      name="phone"
                      required
                      className="mt-1"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="address_line1">{c.address1} *</Label>
                    <Input
                      id="address_line1"
                      name="address_line1"
                      value={shippingInfo.address_line1}
                      onChange={handleInputChange}
                      className="mt-1"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="address_line2">{c.address2}</Label>
                    <Input
                      id="address_line2"
                      name="address_line2"
                      value={shippingInfo.address_line2}
                      onChange={handleInputChange}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">{c.city} *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={shippingInfo.city}
                      onChange={handleInputChange}
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">{c.state}</Label>
                    <Input
                      id="state"
                      name="state"
                      value={shippingInfo.state}
                      onChange={handleInputChange}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="postal_code">{c.postalCode} *</Label>
                    <Input
                      id="postal_code"
                      name="postal_code"
                      value={shippingInfo.postal_code}
                      onChange={handleInputChange}
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">{c.country} *</Label>
                    <Input
                      id="country"
                      name="country"
                      value={shippingInfo.country}
                      onChange={handleInputChange}
                      className="mt-1"
                      required
                    />
                  </div>
                </div>

                {/* Authorization Section */}
                <div className="mt-8 border-t pt-8">
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-bold text-amber-800 text-lg mb-2">{c.authorizationWarning}</h3>
                        <p className="text-amber-700 text-sm">{c.kycNotice}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-6 h-6 rounded-full bg-capsule-teal-light flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-capsule-teal text-sm font-bold">1</span>
                      </div>
                      <p className="text-gray-700 text-sm">{c.term1}</p>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-6 h-6 rounded-full bg-capsule-teal-light flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-capsule-teal text-sm font-bold">2</span>
                      </div>
                      <p className="text-gray-700 text-sm">{c.term2}</p>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-blue-600 text-sm font-bold">3</span>
                      </div>
                      <p className="text-blue-700 text-sm font-medium">{c.term3}</p>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-6 h-6 rounded-full bg-capsule-teal-light flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-capsule-teal text-sm font-bold">4</span>
                      </div>
                      <p className="text-gray-700 text-sm">{c.term4}</p>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-100">
                      <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-red-600 text-sm font-bold">!</span>
                      </div>
                      <p className="text-red-700 text-sm font-medium">{c.term5}</p>
                    </div>
                  </div>

                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-capsule-teal" />
                    {c.authorizationTitle}
                  </h3>

                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    <div className="md:col-span-2">
                      <Label htmlFor="organization_name">{c.organizationName} *</Label>
                      <Input
                        id="organization_name"
                        name="organization_name"
                        value={shippingInfo.organization_name}
                        onChange={handleInputChange}
                        className="mt-1"
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="organization_type">{c.organizationType} *</Label>
                      <select
                        id="organization_type"
                        value={entityType}
                        onChange={(e) => setEntityType(e.target.value)}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-capsule-teal focus:border-transparent"
                        required
                      >
                        <option value="">{c.selectType}</option>
                        <option value="laboratory">{c.laboratory}</option>
                        <option value="clinic">{c.clinic}</option>
                        <option value="university">{c.university}</option>
                        <option value="pharmaceutical">{c.pharmaceutical}</option>
                        <option value="other">{c.other}</option>
                      </select>
                    </div>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer p-4 border-2 border-capsule-teal-light rounded-xl hover:bg-capsule-bg transition-colors">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-1 w-5 h-5 text-capsule-teal border-gray-300 rounded focus:ring-capsule-teal"
                    />
                    <span className="text-gray-700 text-sm leading-relaxed">
                      {c.termsAgreement}
                    </span>
                  </label>
                </div>

                <div className="flex gap-4 mt-8">
                  <Button variant="outline" onClick={() => navigate('/cart')} className="flex-1">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {c.backToCart}
                  </Button>
                  <Button 
                    onClick={handleContinueToReview} 
                    disabled={!agreedToTerms || !entityType}
                    className="flex-1 bg-capsule-teal hover:bg-capsule-teal-dark text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {c.continueToReview}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Review */}
            {step === 2 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-capsule-teal" />
                  {c.review}
                </h2>

                {/* Bank Transfer Notice */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Banknote className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-blue-800 text-sm">{c.secureCheckout}</h4>
                      <p className="text-blue-700 text-xs mt-1">{c.paymentNote}</p>
                    </div>
                  </div>
                </div>

                {/* Shipping Address Summary */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h3 className="font-semibold text-gray-900 mb-2">{c.shippingStep}</h3>
                  <p className="text-gray-600">
                    {shippingInfo.full_name}<br />
                    {shippingInfo.organization_name} ({entityType})<br />
                    {shippingInfo.address_line1}<br />
                    {shippingInfo.address_line2 && <>{shippingInfo.address_line2}<br /></>}
                    {shippingInfo.city}, {shippingInfo.state} {shippingInfo.postal_code}<br />
                    {shippingInfo.country}<br />
                    {shippingInfo.phone}
                  </p>
                </div>

                {/* Order Items */}
                <div className="space-y-4 mb-6">
                  {cartItems.map((item) => (
                    <div key={item.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                      <img 
                        src={item.product?.image_url} 
                        alt={getProductName(item)}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{getProductName(item)}</h4>
                        <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                      </div>
                      <p className="font-bold text-capsule-teal">
                        {formatPrice((item.product?.price || 0) * item.quantity)}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="flex gap-4">
                  <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {c.backToShipping}
                  </Button>
                  <Button 
                    onClick={handlePlaceOrder} 
                    disabled={loading}
                    className="flex-1 bg-capsule-teal hover:bg-capsule-teal-dark text-white"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('checkout_processing')}
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-2" />
                        {c.placeOrder}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Confirmation */}
            {step === 3 && order && (
              <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 mx-auto max-w-2xl">
                <div className="text-center mb-6 sm:mb-8">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                    <Clock className="w-8 h-8 sm:w-10 sm:h-10 text-amber-500" />
                  </div>
                  
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{c.thankYou}</h2>
                  <p className="text-sm sm:text-base text-gray-600 mb-2">{c.orderEmail} {shippingInfo.email}</p>
                  <p className="text-amber-600 font-medium text-sm sm:text-base">{c.pendingReview}</p>
                  
                  <div className="bg-capsule-bg rounded-lg p-4 my-4 sm:my-6 inline-block">
                    <p className="text-xs sm:text-sm text-gray-600">{c.orderNumber}</p>
                    <p className="text-xl sm:text-2xl font-bold text-capsule-teal">{order.id}</p>
                  </div>
                </div>

                {/* What happens next */}
                <div className="bg-gray-50 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center justify-center sm:justify-start gap-2 text-sm sm:text-base">
                    <FileText className="w-5 h-5 text-capsule-teal" />
                    {c.whatHappensNext}
                  </h3>
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-capsule-teal-light flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-capsule-teal text-xs sm:text-sm font-bold">1</span>
                      </div>
                      <p className="text-gray-700 text-xs sm:text-sm">{c.step1Next}</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-capsule-teal-light flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-capsule-teal text-xs sm:text-sm font-bold">2</span>
                      </div>
                      <p className="text-gray-700 text-xs sm:text-sm">{c.step2Next}</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-capsule-teal-light flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-capsule-teal text-xs sm:text-sm font-bold">3</span>
                      </div>
                      <p className="text-gray-700 text-xs sm:text-sm">{c.step3Next}</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-capsule-teal-light flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-capsule-teal text-xs sm:text-sm font-bold">4</span>
                      </div>
                      <p className="text-gray-700 text-xs sm:text-sm">{c.step4Next}</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                  <Button variant="outline" onClick={() => navigate('/products')} className="w-full sm:w-auto">
                    {c.continueShopping}
                  </Button>
                  <Button onClick={() => navigate('/orders')} className="w-full sm:w-auto bg-capsule-teal hover:bg-capsule-teal-dark text-white">
                    {c.viewOrders}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Order Summary Sidebar */}
          {step !== 3 && (
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24">
                <h3 className="text-lg font-bold text-gray-900 mb-4">{c.orderSummary}</h3>
                
                <div className="space-y-3 mb-4">
                  {cartItems.slice(0, 3).map((item) => (
                    <div key={item.id} className="flex items-center gap-3">
                      <img 
                        src={item.product?.image_url} 
                        alt={getProductName(item)}
                        className="w-12 h-12 object-cover rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{getProductName(item)}</p>
                        <p className="text-xs text-gray-500">x{item.quantity}</p>
                      </div>
                      <p className="text-sm font-bold text-gray-900">
                        {formatPrice((item.product?.price || 0) * item.quantity)}
                      </p>
                    </div>
                  ))}
                  {cartItems.length > 3 && (
                    <p className="text-sm text-gray-500 text-center">
                      +{cartItems.length - 3} more {c.items}
                    </p>
                  )}
                </div>

                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{c.subtotal}</span>
                    <span className="font-medium">{formatPrice(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{c.shippingCost}</span>
                    <span className={`font-medium ${shippingCost === 0 ? 'text-green-600' : ''}`}>
                      {shippingCost === 0 ? c.freeShipping : formatPrice(shippingCost)}
                    </span>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t">
                    <span>{c.total}</span>
                    <span className="text-capsule-teal">{formatPrice(total)}</span>
                  </div>
                  <p className="text-xs text-gray-500 text-center">{c.taxIncluded}</p>
                </div>

                {subtotal < 100 && (
                  <div className="mt-4" data-testid="checkout-free-shipping-tracker">
                    <FreeShippingBadge variant="banner" subtotalEur={subtotal} />
                  </div>
                )}
                {subtotal >= 100 && (
                  <div className="mt-4" data-testid="checkout-free-shipping-unlocked">
                    <FreeShippingBadge variant="banner" subtotalEur={subtotal} />
                  </div>
                )}

                <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
                  <Banknote className="w-3 h-3" />
                  {c.secureCheckout}
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
